import { Card, Divider, Tab, Tabs } from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { ThreadPayload } from '@/patient-app-common/types/SecureMessageThreads';

interface ThreadListProps {
  selectedThread: ThreadPayload | undefined;
  threads: ThreadPayload[];
  handleThreadClick: (id: number | null) => void;
}

export function ThreadList({
  selectedThread,
  threads,
  handleThreadClick,
}: ThreadListProps) {
  const { t } = useTranslation('inbox');

  const initTab = threads[0]?.resolved ? 'closed' : 'open';
  const [tab, setTab] = useState<string>(initTab);
  const [openThreads, closedThreads] = _.partition(threads, [
    'resolved',
    false,
  ]);

  useEffect(() => {
    if (!selectedThread) return;

    if (!selectedThread.resolved) {
      setTab('open');
    } else {
      setTab('closed');
    }
  }, [selectedThread]);

  return (
    <div>
      <Card className="bg-warning-200 m-4" radius="sm" shadow="none">
        <p className="m-4 text-warning-900 text-sm">
          {t('thread.banner_text')}
        </p>
      </Card>
      <Tabs
        color="primary"
        fullWidth
        size="md"
        selectedKey={tab}
        onSelectionChange={(key) => {
          setTab(key.toString());
          handleThreadClick(null);
        }}
        className="p-4"
      >
        <Tab key="open" title={t('inbox.subheader.open_threads')}>
          {openThreads.map((thread) => (
            <div
              key={thread.id}
              className={
                selectedThread?.id === thread.id ? `bg-primary-100` : `bg-none`
              }
              role="link"
              tabIndex={0}
              onClick={() => handleThreadClick(thread.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleThreadClick(thread.id);
                }
              }}
            >
              <ThreadListItem thread={thread} />
            </div>
          ))}
        </Tab>
        <Tab key="closed" title={t('inbox.subheader.closed_threads')}>
          {closedThreads.map((thread) => (
            <div
              key={thread.id}
              className={
                selectedThread?.id === thread.id ? `bg-primary-100` : `bg-none`
              }
              role="link"
              tabIndex={0}
              onClick={() => handleThreadClick(thread.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleThreadClick(thread.id);
                }
              }}
            >
              <ThreadListItem thread={thread} />
            </div>
          ))}
        </Tab>
      </Tabs>
    </div>
  );
}

function ThreadListItem({ thread }: { thread: ThreadPayload }) {
  const { t } = useTranslation();

  let displayDate = dayjs(thread.lastMessageSentAt).format('MMM D');

  if (dayjs(thread.lastMessageSentAt).isSame(dayjs(), 'day')) {
    displayDate = dayjs(thread.lastMessageSentAt).format('hh:mm a');
  }

  return (
    <>
      <div className="cursor-pointer p-4">
        <div className="flex justify-between">
          <p
            className={
              thread.unreadMessage
                ? 'font-bold truncate'
                : 'font-normal truncate'
            }
          >
            {_.last(thread.messages)?.sentByName}
          </p>
          <p className={thread.unreadMessage ? 'font-semibold' : 'font-light'}>
            <span className="text-sm">{displayDate}</span>
            <span className="inline-flex align-middle ml-2">
              <Icon
                icon="radix-icons:dot-filled"
                className={thread.unreadMessage ? 'text-danger-500' : 'hidden'}
              />
            </span>
          </p>
        </div>
        <p className={thread.unreadMessage ? 'font-semibold' : 'font-light'}>
          <span className="truncate block">
            {_.last(thread.messages)?.text}
          </span>
          {_.last(thread.messages)?.file && (
            <span className="italic">{t('common.text.attachment')}</span>
          )}
        </p>
      </div>
      <Divider />
    </>
  );
}
